/**
 * 政策系统状态管理
 */

import { create } from 'zustand'
import type {
  PolicyState,
  Policy,
  PolicyEvent,
  PolicyCategory,
  PolicyType
} from '../types/policy'

// 初始状态
const initialState: PolicyState = {
  policies: new Map(),
  events: new Map(),
  categories: new Map(),
  statistics: {
    totalPolicies: 0,
    activePolicies: 0,
    expiredPolicies: 0,
    repealedPolicies: 0,
    totalEvents: 0
  }
}

// 预设政策类别
const policyCategories: PolicyCategory[] = [
  {
    id: 'category_economic',
    name: '经济政策',
    description: '影响国家经济发展的政策',
    policyIds: [],
    icon: '💰'
  },
  {
    id: 'category_military',
    name: '军事政策',
    description: '影响军队和国防的政策',
    policyIds: [],
    icon: '⚔️'
  },
  {
    id: 'category_cultural',
    name: '文化政策',
    description: '影响文化和教育的政策',
    policyIds: [],
    icon: '📚'
  },
  {
    id: 'category_diplomatic',
    name: '外交政策',
    description: '影响国际关系的政策',
    policyIds: [],
    icon: '🤝'
  },
  {
    id: 'category_internal',
    name: '内政政策',
    description: '影响国内治理的政策',
    policyIds: [],
    icon: '🏛️'
  },
  {
    id: 'category_agricultural',
    name: '农业政策',
    description: '影响农业生产的政策',
    policyIds: [],
    icon: '🌾'
  }
]

// 预设政策
const presetPolicies: Omit<Policy, 'id' | 'proposedAt' | 'voting'>[] = [
  {
    name: '井田制',
    type: 'agricultural',
    description: '实行井田制度，规范土地分配，提高农业生产效率',
    effects: [
      {
        type: 'resource_bonus',
        target: 'food',
        value: 20,
        description: '粮食产量 +20%'
      },
      {
        type: 'population_effect',
        target: 'growth',
        value: 5,
        description: '人口增长 +5%'
      }
    ],
    cost: {
      money: 1000,
      resources: {
        food: 500,
        wood: 300
      },
      time: 30,
      approval: 60
    },
    requirements: {
      minStability: 50,
      minEconomy: 30
    },
    status: 'proposed',
    priority: 'high',
    proposerId: 'char_zhou_wuwang',
    implementerId: 'char_zhou_wuwang',
    scope: 'global',
    history: {
      events: [],
      changes: []
    }
  },
  {
    name: '分封制',
    type: 'internal',
    description: '实行分封制度，将土地分封给亲属和功臣，加强对地方的控制',
    effects: [
      {
        type: 'diplomatic_effect',
        target: 'loyalty',
        value: 15,
        description: '诸侯忠诚度 +15%'
      },
      {
        type: 'military',
        target: 'recruitment',
        value: 10,
        description: '征兵速度 +10%'
      }
    ],
    cost: {
      money: 2000,
      resources: {
        metal: 500,
        wood: 800
      },
      time: 60,
      approval: 70
    },
    requirements: {
      minMilitary: 40,
      minStability: 60
    },
    status: 'proposed',
    priority: 'urgent',
    proposerId: 'char_zhou_wuwang',
    implementerId: 'char_zhou_wuwang',
    scope: 'global',
    history: {
      events: [],
      changes: []
    }
  },
  {
    name: '礼乐制度',
    type: 'cultural',
    description: '建立礼乐制度，规范社会等级和行为规范，加强文化认同',
    effects: [
      {
        type: 'cultural_effect',
        target: 'stability',
        value: 10,
        description: '社会稳定 +10%'
      },
      {
        type: 'diplomatic_effect',
        target: 'prestige',
        value: 15,
        description: '国际声望 +15%'
      }
    ],
    cost: {
      money: 1500,
      resources: {
        wood: 500
      },
      time: 45,
      approval: 50
    },
    requirements: {
      minStability: 40
    },
    status: 'proposed',
    priority: 'medium',
    proposerId: 'char_zhou_gong',
    implementerId: 'char_zhou_gong',
    scope: 'global',
    history: {
      events: [],
      changes: []
    }
  }
]

interface PolicyStore extends PolicyState {
  // 初始化
  init: () => void

  // 政策管理
  proposePolicy: (name: string, type: PolicyType, description: string, effects: Policy['effects'], cost: Policy['cost']) => string
  voteOnPolicy: (policyId: string, voterId: string, vote: 'yes' | 'no' | 'abstain') => void
  approvePolicy: (policyId: string) => void
  implementPolicy: (policyId: string) => void
  repealPolicy: (policyId: string) => void

  // 事件管理
  createPolicyEvent: (policyId: string, type: PolicyEvent['type'], description: string, details: Record<string, any>) => void

  // 统计
  updateStatistics: () => void
}

export const usePolicyStore = create<PolicyStore>((set, get) => ({
  ...initialState,
  
  // 初始化
  init: () => {
    set(state => {
      const newCategories = new Map<string, PolicyCategory>()
      const newPolicies = new Map<string, Policy>()
      
      // 初始化类别
      policyCategories.forEach(category => {
        newCategories.set(category.id, category)
      })
      
      // 初始化预设政策
      presetPolicies.forEach(policy => {
        const id = `policy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        const now = { year: 1046, month: 1, day: 1 }
        
        newPolicies.set(id, {
          ...policy,
          id,
          proposedAt: now,
          voting: {
            totalVotes: 0,
            yesVotes: 0,
            noVotes: 0,
            abstentions: 0,
            voters: []
          }
        })
        
        // 更新类别
        const category = newCategories.get(`category_${policy.type}`)
        if (category) {
          newCategories.set(category.id, {
            ...category,
            policyIds: [...category.policyIds, id]
          })
        }
      })
      
      return {
        categories: newCategories,
        policies: newPolicies,
        statistics: {
          ...state.statistics,
          totalPolicies: newPolicies.size
        }
      }
    })
  },
  
  // 政策管理
  proposePolicy: (name, type, description, effects, cost) => {
    const id = `policy_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    
    set(state => {
      const newPolicies = new Map(state.policies)
      newPolicies.set(id, {
        id,
        name,
        type,
        description,
        effects,
        cost,
        requirements: {},
        status: 'proposed',
        priority: 'medium',
        voting: {
          totalVotes: 0,
          yesVotes: 0,
          noVotes: 0,
          abstentions: 0,
          voters: []
        },
        proposedAt: now,
        proposerId: 'char_zhou_wuwang',
        implementerId: 'char_zhou_wuwang',
        scope: 'global',
        history: {
          events: [],
          changes: []
        }
      })
      
      // 更新类别
      const newCategories = new Map(state.categories)
      const category = newCategories.get(`category_${type}`)
      if (category) {
        newCategories.set(category.id, {
          ...category,
          policyIds: [...category.policyIds, id]
        })
      }
      
      return {
        policies: newPolicies,
        categories: newCategories,
        statistics: {
          ...state.statistics,
          totalPolicies: state.statistics.totalPolicies + 1
        }
      }
    })
    
    return id
  },
  
  voteOnPolicy: (policyId, voterId, vote) => {
    set(state => {
      const newPolicies = new Map(state.policies)
      const policy = newPolicies.get(policyId)
      
      if (policy && !policy.voting.voters.includes(voterId)) {
        let yesVotes = policy.voting.yesVotes
        let noVotes = policy.voting.noVotes
        let abstentions = policy.voting.abstentions
        
        switch (vote) {
          case 'yes':
            yesVotes++
            break
          case 'no':
            noVotes++
            break
          case 'abstain':
            abstentions++
            break
        }
        
        newPolicies.set(policyId, {
          ...policy,
          voting: {
            totalVotes: policy.voting.totalVotes + 1,
            yesVotes,
            noVotes,
            abstentions,
            voters: [...policy.voting.voters, voterId]
          }
        })
        
        return { policies: newPolicies }
      }
      
      return state
    })
  },
  
  approvePolicy: (policyId) => {
    set(state => {
      const newPolicies = new Map(state.policies)
      const policy = newPolicies.get(policyId)
      
      if (policy) {
        newPolicies.set(policyId, {
          ...policy,
          status: 'approved',
          approvedAt: { year: 1046, month: 1, day: 1 }
        })
        
        return { policies: newPolicies }
      }
      
      return state
    })
  },
  
  implementPolicy: (policyId) => {
    set(state => {
      const newPolicies = new Map(state.policies)
      const policy = newPolicies.get(policyId)
      
      if (policy) {
        newPolicies.set(policyId, {
          ...policy,
          status: 'implemented',
          implementedAt: { year: 1046, month: 1, day: 1 }
        })
        
        return { 
          policies: newPolicies,
          statistics: {
            ...state.statistics,
            activePolicies: state.statistics.activePolicies + 1
          }
        }
      }
      
      return state
    })
  },
  
  repealPolicy: (policyId) => {
    set(state => {
      const newPolicies = new Map(state.policies)
      const policy = newPolicies.get(policyId)
      
      if (policy) {
        newPolicies.set(policyId, {
          ...policy,
          status: 'repealed',
          expiredAt: { year: 1046, month: 1, day: 1 }
        })
        
        return { 
          policies: newPolicies,
          statistics: {
            ...state.statistics,
            activePolicies: Math.max(0, state.statistics.activePolicies - 1),
            repealedPolicies: state.statistics.repealedPolicies + 1
          }
        }
      }
      
      return state
    })
  },
  
  // 事件管理
  createPolicyEvent: (policyId, type, description, details) => {
    const id = `event_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    
    set(state => {
      const newEvents = new Map(state.events)
      newEvents.set(id, {
        id,
        policyId,
        type,
        description,
        date: now,
        details
      })
      
      // 更新政策历史
      const newPolicies = new Map(state.policies)
      const policy = newPolicies.get(policyId)
      if (policy) {
        newPolicies.set(policyId, {
          ...policy,
          history: {
            ...policy.history,
            events: [...policy.history.events, id]
          }
        })
      }
      
      return {
        events: newEvents,
        policies: newPolicies,
        statistics: {
          ...state.statistics,
          totalEvents: state.statistics.totalEvents + 1
        }
      }
    })
  },
  
  // 统计
  updateStatistics: () => {
    const state = get()
    let activePolicies = 0
    let expiredPolicies = 0
    let repealedPolicies = 0
    
    Array.from(state.policies.values()).forEach(policy => {
      if (policy.status === 'implemented') activePolicies++
      if (policy.status === 'expired') expiredPolicies++
      if (policy.status === 'repealed') repealedPolicies++
    })
    
    set({
      statistics: {
        ...state.statistics,
        activePolicies,
        expiredPolicies,
        repealedPolicies
      }
    })
  }
}))

// 初始化政策系统
usePolicyStore.getState().init()
